import React, { useState, CSSProperties, useEffect } from "react";
import { Card, Toast, ToastContainer } from "react-bootstrap";
import AvatarUpload from "components/common/AvatarUpload";
import CoverUpload from "components/common/CoverUpload";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAppContext } from "providers/AppProvider";
import { useTranslation } from "react-i18next";
import coverPlaceholder from "assets/img/profileAndCover/Cover.jpg";
import profilePlaceholder from "assets/img/profileAndCover/Profil.jpg";
import { useProfileUpdater } from "hooks/useProfileUpdater";
import ImagePreviewModal from "components/modals/ImagePreviewModal";
import { useMutation } from '@apollo/client';
import { UPDATE_SELLER_PROFILE } from 'api/graphql/mutations/seller/updateProfile';
import { fileToBase64 } from 'utils/imageUtils';
import { useAuth } from 'providers/AuthProvider';

interface SellerProfileCardProps {
  seller: {
    name: string;
    company_locality: string;
    shop_url: string;
    shop_title: string;
    total_spent?: string;
    last_order?: string;
    total_orders?: string;
    logo_pic?: string;
    banner_pic?: string;
    company_description?: string;
  };
  onUpdateSellerInfo: (
    updatedTitle: string,
    updatedDescription: string
  ) => void;
  onUpdateProfile?: (input: any) => Promise<void>;
  onProfileUpdated?: () => void;
  isLoading?: boolean;
}

const SellerProfileCard: React.FC<SellerProfileCardProps> = ({
  seller,
  onUpdateSellerInfo,
  onUpdateProfile,
  onProfileUpdated,
  isLoading,
}) => {
  const { t } = useTranslation();
  const {
    config: { isDark },
  } = useAppContext();
  
  // Access context seller data directly
  const { seller: contextSeller, setSeller } = useAuth();
  
  // State for image previews - will show immediately when files are selected
  const [coverPhoto, setCoverPhoto] = useState<string | null>(
    seller.banner_pic
      ? `${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${seller.banner_pic}`
      : coverPlaceholder
  );
  
  // State for avatar preview - only for temporary visual feedback
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    seller.logo_pic
      ? `${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${seller.logo_pic}`
      : profilePlaceholder
  );
  
  // Update previews when context seller data changes
  useEffect(() => {
    if (contextSeller) {
      if (contextSeller.logo_pic && contextSeller.logo_pic !== seller.logo_pic) {
        setAvatarPreview(`${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${contextSeller.logo_pic}`);
      }
      
      if (contextSeller.banner_pic && contextSeller.banner_pic !== seller.banner_pic) {
        setCoverPhoto(`${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${contextSeller.banner_pic}`);
      }
      
      // Notify parent about title/description updates if they've changed in context
      if (contextSeller.shop_title !== seller.shop_title || 
          contextSeller.company_description !== seller.company_description) {
        onUpdateSellerInfo(
          contextSeller.shop_title || seller.shop_title,
          contextSeller.company_description || seller.company_description
        );
      }
    }
  }, [contextSeller, seller, onUpdateSellerInfo]);
  
  // Use the profile updater hook with refetchProfileData
  const {
    trackChange,
    refetchProfileData,
    isSubmitting,
    showToast,
    setShowToast,
    toastMessage,
    toastVariant
  } = useProfileUpdater({
    onSuccess: () => {
      // Profile updates handled by the hook
    }
  });
  
  // Local state for toast messages
  const [localToastMessage, setLocalToastMessage] = useState('');
  const [localToastVariant, setLocalToastVariant] = useState<'success' | 'error'>('success');
  
  // GraphQL mutation for direct profile updates
  const [updateProfile, { loading: isMutationLoading }] = useMutation(UPDATE_SELLER_PROFILE, {
    onCompleted: (data) => {
      if (data.updateSellerProfile.success) {
        // Show success message using local toast
        setLocalToastMessage(t('updateSuccess'));
        setLocalToastVariant('success');
        setShowToast(true);
        
        // Update local state with returned data if available
        if (data.updateSellerProfile.data) {
          const updatedData = data.updateSellerProfile.data;
          
          // Update avatar and cover previews with the new paths
          if (updatedData.logo_pic) {
            setAvatarPreview(`${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${updatedData.logo_pic}`);
          }
          
          if (updatedData.banner_pic) {
            setCoverPhoto(`${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${updatedData.banner_pic}`);
          }
          
          // Update auth context with the new data
          if (contextSeller && setSeller) {
            const updatedSeller = {
              ...contextSeller,
              ...updatedData
            };
            setSeller(updatedSeller);
            localStorage.setItem('seller', JSON.stringify(updatedSeller));
          }
        } else {
          // Fallback to refetching if data is not included in the response
          refetchProfileData();
        }
        
        // Notify parent component about the profile update
        if (onProfileUpdated) {
          onProfileUpdated();
        }
      } else {
        // Show error message
        setLocalToastMessage(data.updateSellerProfile.message || t('updateError'));
        setLocalToastVariant('error');
        setShowToast(true);
      }
    },
    onError: (error) => {
      console.error('Error updating profile image:', error);
      setLocalToastMessage(t('updateError'));
      setLocalToastVariant('error');
      setShowToast(true);
    }
  });
  
  // Loading states
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [coverLoading, setCoverLoading] = useState(false);

  // Track selected files in state to save later
  const [pendingFiles, setPendingFiles] = useState<{
    logo_pic?: File;
    banner_pic?: File;
  }>({});
  
  // State for modal control
  const [showModal, setShowModal] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [currentImageType, setCurrentImageType] = useState<'avatar' | 'cover'>('avatar');

  // Function to handle avatar file selection and open preview modal
  const handleAvatarChange = async (file: File | null) => {
    if (!file) return;

    setAvatarLoading(true);
    try {
      // Create object URL for preview
      const objectUrl = URL.createObjectURL(file);
      
      // Set preview image for the modal
      setPreviewImageSrc(objectUrl);
      setPreviewTitle(t('previewAvatar'));
      setCurrentImageType('avatar');
      
      // Track the file temporarily
      setPendingFiles(prev => ({
        ...prev,
        logo_pic: file
      }));
      
      // Open the modal to preview the image
      setShowModal(true);
      
      // Also set the preview in the avatar component for immediate visual feedback
      setAvatarPreview(objectUrl);
    } catch (error) {
      console.error("Error handling avatar:", error);
      // Fall back to original avatar if there's an error
      setAvatarPreview(seller.logo_pic 
        ? `${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${seller.logo_pic}`
        : profilePlaceholder);
    } finally {
      setAvatarLoading(false);
    }
  };

  // Function to handle cover photo file selection and open preview modal
  const handleCoverChange = async (file: File | null) => {
    if (!file) return;

    setCoverLoading(true);
    try {
      // Create object URL for preview
      const objectUrl = URL.createObjectURL(file);
      
      // Set preview image for the modal
      setPreviewImageSrc(objectUrl);
      setPreviewTitle(t('previewCover'));
      setCurrentImageType('cover');
      
      // Track the file temporarily
      setPendingFiles(prev => ({
        ...prev,
        banner_pic: file
      }));
      
      // Open the modal to preview the image
      setShowModal(true);
      
      // Also set the preview in the cover component for immediate visual feedback
      setCoverPhoto(objectUrl);
    } catch (error) {
      console.error("Error handling cover photo:", error);
      // Fall back to original cover if there's an error
      setCoverPhoto(seller.banner_pic 
        ? `${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${seller.banner_pic}`
        : coverPlaceholder);
    } finally {
      setCoverLoading(false);
    }
  };
  
  // Function to handle confirmation from the modal - now uses mutation directly
  const handleImageConfirm = async () => {
    // Get the current file being confirmed
    const file = currentImageType === 'avatar' 
      ? pendingFiles.logo_pic 
      : pendingFiles.banner_pic;
    
    if (file) {
      try {
        // Set loading state
        currentImageType === 'avatar' ? setAvatarLoading(true) : setCoverLoading(true);
        
        // Convert file to base64 for API submission
        const fileBase64 = await fileToBase64(file);
        
        // Prepare input based on image type
        const input: Record<string, any> = {
          [currentImageType === 'avatar' ? 'logo_pic' : 'banner_pic']: fileBase64
        };
        
        // Submit the mutation directly
        await updateProfile({
          variables: {
            input
          }
        });
        
        // Also track the change for the central state management
        trackChange(currentImageType === 'avatar' ? 'logo_pic' : 'banner_pic', file);
      } catch (error) {
        console.error('Error processing image update:', error);
        setLocalToastMessage(t('updateError'));
        setLocalToastVariant('error');
        setShowToast(true);
      } finally {
        // Reset loading state
        currentImageType === 'avatar' ? setAvatarLoading(false) : setCoverLoading(false);
      }
    }
    
    // Close the modal
    setShowModal(false);
  };
  
  // Function to handle cancellation from the modal
  const handleModalHide = () => {
    // If canceled, revert to the original image
    if (currentImageType === 'avatar') {
      setAvatarPreview(seller.logo_pic 
        ? `${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${seller.logo_pic}`
        : profilePlaceholder);
        
      // Remove the pending file
      setPendingFiles(prev => {
        const { logo_pic, ...rest } = prev;
        return rest;
      });
    } else {
      setCoverPhoto(seller.banner_pic 
        ? `${process.env.REACT_APP_MEDIA_SERVER_API_URL}/avatar/${seller.banner_pic}`
        : coverPlaceholder);
        
      // Remove the pending file
      setPendingFiles(prev => {
        const { banner_pic, ...rest } = prev;
        return rest;
      });
    }
    
    // Close the modal
    setShowModal(false);
  };

  const styles: { [key: string]: CSSProperties } = {
    coverPhoto: {
      position: "relative",
      width: "100%",
      height: "232px",
      overflow: "hidden",
      borderRadius: "0.5rem",
    },
    profileSection: {
      position: "relative",
      top: "-32px",
      textAlign: "left",
      display: "flex",
      alignItems: "center",
    },
    statsSection: {
      paddingTop: "20px",
      marginTop: "20px",
      borderTop: "1px solid #eee",
    },
  };
  
  // Clean up object URLs when component unmounts to prevent memory leaks
  React.useEffect(() => {
    return () => {
      // Check if the URLs are object URLs before revoking
      if (coverPhoto && coverPhoto.startsWith('blob:')) {
        URL.revokeObjectURL(coverPhoto);
      }
      if (avatarPreview && avatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [coverPhoto, avatarPreview]);

  // Use the context seller data for display if available, fallback to prop data
  const displayTitle = contextSeller?.shop_title || seller.shop_title;
  const displayDescription = contextSeller?.company_description || seller.company_description;

  return (
    <>
      <Card className="h-100">
        <div style={styles.coverPhoto}>
          {coverLoading ? (
            <Skeleton
              height="232px"
              baseColor="var(--phoenix-body-bg)"
              highlightColor={isDark ? "#444" : "#eee"}
            />
          ) : (
            coverPhoto && (
              <CoverUpload
                src={coverPhoto}
                className="w-100"
                onChange={handleCoverChange}
                aria-label={t("uploadCover")}
              />
            )
          )}
        </div>
        <Card.Body>
          <div style={styles.profileSection}>
            <div
              style={{
                borderRadius: "50%",
                border: "4px solid var(--phoenix-emphasis-bg)",
              }}
            >
              {avatarLoading ? (
                <Skeleton
                  circle
                  height="9.375rem"
                  width="9.375rem"
                  baseColor="var(--phoenix-body-bg)"
                  highlightColor={isDark ? "#444" : "#eee"}
                />
              ) : (
                <AvatarUpload
                  size="5xl"
                  src={avatarPreview || profilePlaceholder}
                  onChange={handleAvatarChange}
                  aria-label={t("uploadAvatar")}
                />
              )}
            </div>
            <div style={{ padding: "16px" }}>
              <h3>{displayTitle}</h3>
              <p className="text-body-secondary">
                {displayDescription}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
      >
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          bg={localToastVariant === "success" ? "success" : "danger"}
          delay={3000}
          autohide
        >
          <Toast.Body>{localToastMessage || toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      
      {/* Image Preview Modal */}
      <ImagePreviewModal
        show={showModal}
        onHide={handleModalHide}
        onConfirm={handleImageConfirm}
        imageSrc={previewImageSrc}
        title={previewTitle}
      />
    </>
  );
};

export default SellerProfileCard;
